import type { AxiosError } from 'axios';
import apiClient from '@/api/_axios';
import { ownAccount } from '@/features/community/http';

/**
 * The signed-in person's own Profile (/api/profile, Ray 2026-10-25).
 * Always as MYSELF: the server refuses X-Act-As here, so a helper or SmartFi
 * staff inside another account still edits their own profile.
 */
export interface MyProfile {
   firstName: string;
   lastName: string;
   name: string;
   bio: string;
   avatar: string | null;
   cover: string | null;
   /** Shown on their chat card; the same link the chat's Verify form takes (Ray 2026-10-26). */
   facebookUrl?: string;
   canChangeAvatar: boolean;
}
export type ProfilePatch = Partial<Pick<MyProfile, 'firstName' | 'lastName' | 'bio' | 'avatar' | 'cover' | 'facebookUrl'>>;

/** The server's own words ("Enter your first name."), or a plain fallback. */
export const profileError = (e: unknown, fallback = 'Could not save. Try again.') => {
   const b = (e as AxiosError<{ error?: string; err?: string; msg?: string }>)?.response?.data;
   return (b && (b.error || b.err || b.msg)) || fallback;
};

export async function getMyProfile(): Promise<MyProfile> {
   return (await apiClient.get('/profile', ownAccount())).data.profile;
}

export async function patchMyProfile(patch: ProfilePatch): Promise<MyProfile> {
   return (await apiClient.patch('/profile', patch, ownAccount())).data.profile;
}

/** Upload a cover or avatar: one-time link from the server, PUT the picture, then save its address. */
export async function uploadProfilePhoto(which: 'avatar' | 'cover', file: File): Promise<MyProfile> {
   const sign = (await apiClient.post('/profile/photo', { which, type: file.type, size: file.size }, ownAccount())).data as {
      uploadUrl: string;
      publicUrl: string;
      cacheControl?: string;
   };
   const put = await fetch(sign.uploadUrl, {
      method: 'PUT',
      // cache-control is part of what the server signed.
      headers: { 'content-type': file.type, ...(sign.cacheControl ? { 'cache-control': sign.cacheControl } : {}) },
      body: file,
   });
   if (!put.ok) throw new Error(`The upload failed (HTTP ${put.status}). Try again.`);
   return patchMyProfile({ [which]: sign.publicUrl });
}
